import type { ReactNode } from 'react';
import { Menu as M } from '@base-ui/react/menu';
import { cn } from '../lib/utils';

/**
 * Dropdown menu. Base UI is kept here because it provides the menu keyboard model
 * (arrow keys, typeahead, focus return) that a hand-written popover would lose.
 * Styled as a DESIGN.md floating layer (tooltip elevation).
 */
export const MenuRoot = M.Root;
export const MenuTrigger = M.Trigger;
export const MenuGroup = M.Group;
export const MenuRadioGroup = M.RadioGroup;

export function MenuPopup({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  return (
    <M.Portal>
      <M.Positioner className="z-50 outline-none" align={align} sideOffset={6}>
        <M.Popup
          className={cn(
            'max-h-(--available-height) min-w-56 overflow-y-auto rounded-lg border border-[var(--line)] bg-[var(--surface)] p-1 text-sm text-[var(--ink)] shadow-[var(--shadow-tooltip)] outline-none',
            className,
          )}
        >
          {children}
        </M.Popup>
      </M.Positioner>
    </M.Portal>
  );
}

const itemClass =
  'flex min-h-9 cursor-default items-center gap-2 rounded-md px-2 py-1.5 outline-none select-none data-[highlighted]:bg-[var(--surface-muted)] data-[disabled]:opacity-50';

export function MenuItem({
  className,
  tone = 'default',
  ...props
}: M.Item.Props & { tone?: 'default' | 'negative' }) {
  return (
    <M.Item
      className={cn(
        itemClass,
        tone === 'negative' && 'text-[var(--negative)]',
        className as string,
      )}
      {...props}
    />
  );
}

export function MenuRadioItem({ className, children, ...props }: M.RadioItem.Props) {
  return (
    <M.RadioItem className={cn(itemClass, 'pr-8', className as string)} {...props}>
      <span className="flex-1">{children}</span>
      <M.RadioItemIndicator className="text-xs text-[var(--ink-soft)]">✓</M.RadioItemIndicator>
    </M.RadioItem>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <M.GroupLabel className="px-2 py-1.5 text-xs font-medium text-[var(--ink-faint)]">
      {children}
    </M.GroupLabel>
  );
}

export function MenuSeparator() {
  return <M.Separator className="-mx-1 my-1 h-px bg-[var(--line)]" />;
}
